/**
 * 계약 건물 정보 컴포넌트
 * 계약서의 건물 정보를 표시하는 컴포넌트입니다.
 * 건물명, 층, 호실 등의 정보를 테이블 형태로 표시합니다.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useBlockLayout, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import { getContractProductSnapshotDetailPopup } from "src/api/contract/contract-api";
import { ContractBuildingRoomForTable, DetailTotalData } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { buildingsDetails } from "src/api/product/product-api";
import { BaseTooltip } from "src/components";
import { ArticleSection } from "src/components/layout/ArticleSection";

/**
 * 테이블 컬럼 정의
 */
export const columns: any = [
  {
    Header: "id",
    accessor: "buildingId",
    // sticky: "left",
    Cell: ({ value }: any) => {
      return <>{value && value}</>;
    },
    width: 70,
  },
  {
    Header: "건물명",
    accessor: "buildingName",
    Cell: ({ value }: any) => {
      return (
        <div className="minmax250 text-left ellipsis2">
          <BaseTooltip contents={value} isSingleLine />
        </div>
      );
    },
    width: 250,
  },

  {
    Header: "층",
    accessor: "floorName",
    width: 250,
    Cell: ({ value, row }: any) => {
      // console.log(value);
      return (
        <>
          {row.isGround ? "지하 " : "지상 "}
          {value && value}
        </>
      );
    },
  },
  {
    Header: "호",
    accessor: "rooms",
    width: 500,
    Cell: ({ value }: any) => {
      return (
        <>
          <span className="ellipsis2">{value}</span>
        </>
      );
    },
  },
];

/**
 * 계약 건물 정보 컴포넌트의 props 인터페이스
 */
interface ContractBasicProp {
  detailData: DetailTotalData; //상품 상세 조회 정보
}

/**
 * 계약 건물 정보 컴포넌트
 * @param detailData 계약 상세 정보
 */
const ContractBuildingRooms = ({ detailData }: ContractBasicProp) => {
  const { executeAsync: getSnapshot } = useApiOperation(getContractProductSnapshotDetailPopup);
  const { executeAsync: getBuildingsDetails } = useApiOperation(buildingsDetails);

  const [rooms, setRooms] = useState<Array<ContractBuildingRoomForTable>>([]);

  /**
   * 스냅샷 데이터를 건물 상세 데이터와 매칭하여 테이블 형식으로 변환하는 함수
   */
  const concatRooms = (snapshotData: any[], buildingsData: any[]) => {
    // 1. 스냅샷에서 ID 수집
    const snapshotIds = {
      buildings: new Set<string>(),
      floors: new Set<string>(),
      rooms: new Set<string>(),
    };

    snapshotData.forEach((item: any) => {
      const id = String(item.domainId);
      if (item.domain === "building") snapshotIds.buildings.add(id);
      else if (item.domain === "buildingFloor") snapshotIds.floors.add(id);
      else if (item.domain === "buildingRoom") snapshotIds.rooms.add(id);
    });

    // 2. 건물 상세 데이터에서 매칭되는 정보만 추출
    const result: Array<ContractBuildingRoomForTable> = [];

    buildingsData.forEach((building: any) => {
      const buildingId = String(building.id);

      if (!snapshotIds.buildings.has(buildingId)) return;

      building.buildingFloorList?.forEach((floor: any) => {
        const floorId = String(floor.id);

        if (!snapshotIds.floors.has(floorId)) return;

        const rooms =
          floor.buildingRoomList
            ?.filter((room: any) => snapshotIds.rooms.has(String(room.id)))
            ?.map((room: any) => room.roomName || `호실 ${room.id}`) || [];

        if (rooms.length > 0) {
          result.push({
            buildingId,
            buildingName: building.buildingName || `건물 ${buildingId}`,
            floorName: floor.floorName || `층 ${floorId}`,
            isGround: (floor.floorNum || 0) >= 0,
            rooms: rooms.join(", "),
          });
        }
      });
    });

    return result;
  };

  /**
   * 스냅샷의 건물 ID들로 건물 상세 정보를 조회하는 함수
   */
  const getRoomsList = useCallback(async () => {
    if (detailData?.contract?.contractId) {
      try {
        const response: any = await getSnapshot({ contractId: Number(detailData?.contract?.contractId) });

        // 스냅샷에서 건물 ID들 추출
        const buildingItems = response.data.data.content.filter((item: any) => item.domain === "building");
        const buildingIds = buildingItems.map((item: any) => String(item.domainId));

        if (buildingIds.length > 0) {
          // 건물 상세 정보 조회
          const buildingDetails = await getBuildingsDetails({
            building: { ids: buildingIds },
          }).catch(() => undefined);

          if (buildingDetails?.data?.data?.content && buildingDetails.data.data.content.length > 0) {
            const buildings = buildingDetails.data.data.content.map((item: any) => item.building);
            setRooms(concatRooms(response.data.data.content, buildings));
          } else {
            // 건물 상세 정보 조회 실패 시 스냅샷 데이터만으로 처리
            setRooms(concatRooms(response.data.data.content, []));
          }
        } else {
          // 건물 정보가 없는 경우
          setRooms([]);
        }
      } catch (error) {
        console.error("건물 상세 정보 조회 실패:", error);
      }
    }
  }, [detailData?.contract?.contractId, getBuildingsDetails, getSnapshot]);

  /**
   * 테이블 설정
   */
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: rooms,
      initialState: { pageSize: 1000 },
    },
    useBlockLayout,
    useBlockLayout,
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
  );

  /**
   * 테이블 전체 너비 계산
   */
  const tableWidth = useMemo(() => {
    let totalWidth = 0;
    headerGroups.forEach((headerGroup) => {
      headerGroup.headers.forEach((header) => (totalWidth += Number(header?.width || 0)));
    });
    return totalWidth;
  }, [headerGroups]);

  /**
   * 계약 정보가 변경될 때 건물 호실 정보를 조회하는 효과
   */
  useEffect(() => {
    if (detailData?.contract?.contractId) {
      getRoomsList();
    }
  }, [detailData?.contract?.contractId, getRoomsList]);
  return (
    <ArticleSection
      title={"건물 정보"}
      children={
        <div className="contents-container__grid-contents">
          <div>
            <div {...getTableProps()} className="base-table sticky">
              <div className="header">
                {headerGroups.map((headerGroup) => (
                  <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                    {headerGroup.headers.map((header) => {
                      return (
                        <div {...header.getHeaderProps()} className="base-table__th">
                          {header.render("Header")}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div {...getTableBodyProps()} className="body">
                {rows.map((row: any) => {
                  prepareRow(row);

                  return (
                    <div {...row.getRowProps()} className={`base-table__tr`}>
                      {row.cells &&
                        row.cells.map((cell: any, index: number) => {
                          return (
                            <div {...cell.getCellProps()} className="base-table__td">
                              <div>{cell.render("Cell")}</div>
                            </div>
                          );
                        })}
                    </div>
                  );
                })}
                <div>
                  {rows.length === 0 && (
                    <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
                      <div className="base-table__td w-100 text-center">
                        <div className="w-100">{<span>데이터가 없습니다.</span>}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default ContractBuildingRooms;
